"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.mostActiveSubscriptionStatus = mostActiveSubscriptionStatus;
exports.getSubscriptionForProductFromContext = getSubscriptionForProductFromContext;
exports.getSubscriptionForProductFromSubscriptions = getSubscriptionForProductFromSubscriptions;
exports.subscriptionContainsProduct = subscriptionContainsProduct;
exports.getSubscriptionForCopilotCreditsFromContext = getSubscriptionForCopilotCreditsFromContext;
exports.getSubscriptionItemForCopilotCreditsFromContext = getSubscriptionItemForCopilotCreditsFromContext;
const models_1 = require("@buildwithflux/models");
/**
 * Reverse-sorts the given payment subscriptions by their subscription status, according to SubscriptionStatusOrdered
 *
 * The nearer the beginning of SubscriptionStatusOrdered, the more "active" the subscription is, and those statuses
 * will be preferred
 */
function compareByOrderedSubscriptionStatus(a, b) {
    const aIdx = models_1.SubscriptionStatusOrdered.indexOf(a.status);
    const bIdx = models_1.SubscriptionStatusOrdered.indexOf(b.status);
    if (aIdx === bIdx && a.trialEndsAt && b.trialEndsAt) {
        return a.trialEndsAt - b.trialEndsAt;
    }
    return ((bIdx === -1 ? models_1.SubscriptionStatusOrdered.length : bIdx) -
        (aIdx === -1 ? models_1.SubscriptionStatusOrdered.length : aIdx));
}
/**
 * Returns the "most active" subscription status from the given list of statuses
 *
 * Where most active is defined by the order given in SubscriptionStatusOrdered
 */
function mostActiveSubscriptionStatus(statuses) {
    return statuses.sort((a, b) => models_1.SubscriptionStatusOrdered.indexOf(b) - models_1.SubscriptionStatusOrdered.indexOf(a))[0];
}
/**
 * Gets the subscriptions to be searched for a given context and search context
 */
function getSubscriptionsFromSearchingEntitlementContext(context, searchContext) {
    if (searchContext === "user") {
        return {
            ...(context.currentUserPrivateMetadata.payment?.subscriptions ?? {}),
            ...(context.currentUserPrivateMetadata.payment?.copilotCreditSubscriptions ?? {}),
            ...(context.currentUserPrivateMetadata.payment?.addonSubscriptions ?? {}),
            ...(context.currentUserPrivateMetadata.payment?.generatedTrialSubscriptions ?? {}),
        };
    }
    else if (searchContext === "organization" && context.type === "organization") {
        return {
            ...(context.membership.metadata.payment?.subscriptions ?? {}),
            ...(context.membership.metadata.payment?.copilotCreditSubscriptions ?? {}),
            ...(context.membership.metadata.payment?.addonSubscriptions ?? {}),
            ...(context.membership.metadata.payment?.generatedTrialSubscriptions ?? {}),
        };
    }
    else if (searchContext === "enterprise" && context.type === "enterprise") {
        return {
            ...(context.membership.enterprise.defaultOrganization.metadata.payment?.subscriptions ?? {}),
            ...(context.membership.enterprise.defaultOrganization.metadata.payment?.copilotCreditSubscriptions ?? {}),
            ...(context.membership.enterprise.defaultOrganization.metadata.payment?.addonSubscriptions ?? {}),
            ...(context.membership.enterprise.defaultOrganization.metadata.payment?.generatedTrialSubscriptions ?? {}),
        };
    }
    else {
        // Unsupported context type
        return {};
    }
}
/**
 * Searches the context for a subscription to the specified Stripe Product, regardless of whether it's active, but
 * excluding canceled subscriptions
 */
function getSubscriptionForProductFromContext(context, searchContext, productCategory) {
    const productId = context.stripeClientMode === "test"
        ? models_1.PaymentPlanUidsForTestMode[productCategory]
        : models_1.PaymentPlanUids[productCategory];
    if (!productId) {
        return undefined;
    }
    return getSubscriptionForProductFromSubscriptions(getSubscriptionsFromSearchingEntitlementContext(context, searchContext), productId, true);
}
/**
 * Searches the given set of subscriptions for one to the specified Stripe Product, regardless of whether it's active
 *
 * Can optionally filter out canceled subscriptions, or return them
 */
function getSubscriptionForProductFromSubscriptions(subscriptions, product, filterOutCanceled = true) {
    const matchingSubscriptions = Object.values(subscriptions).filter((subscription) => (!filterOutCanceled || subscription.status !== models_1.SubscriptionStatus.enum.canceled) &&
        subscriptionContainsProduct(subscription, product));
    if (!matchingSubscriptions.length) {
        return undefined;
    }
    matchingSubscriptions.sort(compareByOrderedSubscriptionStatus);
    return matchingSubscriptions.pop();
}
/**
 * Returns true if the given subscription contains (one of) the specified Stripe Product(s)
 */
function subscriptionContainsProduct(subscription, product) {
    if (!Array.isArray(product)) {
        product = [product];
    }
    return Object.values(subscription.items ?? {}).some((item) => product.includes(item.price.productUid));
}
// Could also have something like:
// export function subscriptionContainsProduct(subscription: PaymentSubscription, lookupKey: string): boolean
/**
 * Gets the Copilot Credits subscription by searching for it by product
 */
function getSubscriptionForCopilotCreditsFromContext(context, searchContext, stripeClientMode) {
    return getSubscriptionForProductFromSubscriptions(getSubscriptionsFromSearchingEntitlementContext(context, searchContext), stripeClientMode === "test" ? models_1.PaidCreditTestProduct : models_1.PaidCreditProduct, true);
}
/**
 * Gets the Copilot Credits subscription by searching for it by product
 */
function getSubscriptionItemForCopilotCreditsFromContext(context, searchContext, stripeClientMode) {
    const subscription = getSubscriptionForCopilotCreditsFromContext(context, searchContext, stripeClientMode);
    if (!subscription) {
        return undefined;
    }
    return Object.values(subscription.items ?? {}).find((item) => item.price.productUid === (stripeClientMode === "test" ? models_1.PaidCreditTestProduct : models_1.PaidCreditProduct));
}
//# sourceMappingURL=subscription.js.map